import React, { useState } from 'react';
import { EmailTemplateType, SystemConfig } from '@/types';
import { ChevronRight } from 'lucide-react';
import { SimpleEditor, VariableList } from './settingsShared';

interface EmailTemplatesSectionProps {
  config: SystemConfig;
  onUpdate: (config: SystemConfig) => void;
  canEdit: boolean;
}

export const EmailTemplatesSection: React.FC<EmailTemplatesSectionProps> = ({
  config,
  onUpdate,
  canEdit
}) => {
  const [selectedType, setSelectedType] = useState<EmailTemplateType>('booking_confirmation');
  const emailTemplates = config.templates.emailTemplates || [];
  const template = emailTemplates.find((t) => t.type === selectedType);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 animate-fade-in">
      <div className="space-y-1 bg-white p-2 rounded-2xl border border-slate-200 shadow-sm h-fit">
        <p className="px-3 py-2 text-[10px] font-black uppercase text-slate-400">Trigger Events</p>
        {emailTemplates.length === 0 ? (
          <p className="text-xs text-slate-400 italic px-3">No templates found.</p>
        ) : (
          emailTemplates.map((t) => (
            <button
              key={t.type}
              onClick={() => setSelectedType(t.type)}
              className={`w-full text-left p-3 rounded-xl transition-all border-2 text-xs font-bold flex items-center justify-between group ${selectedType === t.type ? 'border-teal-500 bg-teal-50 text-teal-800' : 'border-transparent bg-white text-slate-500 hover:bg-slate-50'}`}
            >
              {t.label}
              <ChevronRight
                className={`w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity ${selectedType === t.type ? 'opacity-100' : ''}`}
              />
            </button>
          ))
        )}
      </div>

      <div className="lg:col-span-3 space-y-6">
        {!template ? (
          <div className="p-12 text-center text-slate-400">Select a trigger from the left</div>
        ) : (
          <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm space-y-6">
            <div className="flex justify-between items-center border-b pb-4">
              <h4 className="font-bold text-lg text-slate-800">Editing: {template.label}</h4>
              <VariableList />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-2">
                Subject Line
              </label>
              <input
                className="w-full border rounded-xl p-3 bg-slate-50 outline-none focus:ring-2 focus:ring-teal-500 font-bold"
                value={template.subject}
                onChange={(e) => {
                  const newTemplates = [...emailTemplates];
                  const idx = newTemplates.findIndex((x) => x.type === selectedType);
                  newTemplates[idx] = { ...template, subject: e.target.value };
                  onUpdate({
                    ...config,
                    templates: { ...config.templates, emailTemplates: newTemplates }
                  });
                }}
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-2">
                Message Body
              </label>
              <SimpleEditor
                height="400px"
                value={template.body}
                onChange={(val) => {
                  const newTemplates = [...emailTemplates];
                  const idx = newTemplates.findIndex((x) => x.type === selectedType);
                  newTemplates[idx] = { ...template, body: val };
                  onUpdate({
                    ...config,
                    templates: { ...config.templates, emailTemplates: newTemplates }
                  });
                }}
                disabled={!canEdit}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
